import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { submissionSchema } from "@/lib/form-schema";
import { getFormSubmissionsClient } from "@/lib/supabase-form-submissions.server";
import { trafficFieldsFromHeaders, trafficTermContentFromHeaders } from "@/lib/traffic-fields";
import { fireEnquiryWebhook } from "@/lib/forms.server";

export const submitEnquiry = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => submissionSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      // Honeypot: silently succeed without writing to DB so bots get no signal.
      // Return a distinct id so client dedup can drop bot-triggered analytics.
      const hasHumanContact = Boolean(
        data.name?.trim() && (data.phone?.trim() || data.email?.trim()),
      );
      if (data.honeypot && data.honeypot.length > 0 && !hasHumanContact) {
        return { success: true as const, id: `honeypot-${crypto.randomUUID()}` };
      }

      const userAgent = getRequestHeader("user-agent")?.slice(0, 1000) ?? null;

      // Server-generated authoritative id, inserted as PK so we do not depend
      // on RLS SELECT to return it. This is the id used for Google Ads
      // transaction_id (Layer 2 dedup) and Layer 1 client dedup.
      const id = crypto.randomUUID();

      const insertRow = {
        id,
        form_type: data.form_type,
        name: data.name?.trim() || null,
        phone: data.phone?.trim() || null,
        email: data.email?.trim() || null,
        postcode: data.postcode?.trim() || null,
        service_of_interest:
          data.services_interested?.length
            ? data.services_interested.join(", ")
            : data.service_of_interest?.trim() || null,
        message: [
          data.homeowner_status?.trim() ? `Homeowner: ${data.homeowner_status.trim()}` : null,
          data.message?.trim() || null,
        ]
          .filter(Boolean)
          .join("\n") || null,
        source_page: data.source_page?.trim() || null,
        gclid: data.gclid?.trim() || null,
        user_agent: userAgent,
        ...trafficFieldsFromHeaders(getRequestHeader),
      };

      const { error } = await getFormSubmissionsClient()
        .from("form_submissions")
        .insert(insertRow);

      if (error) {
        console.error("Failed to insert form submission:", error);
        return {
          success: false as const,
          error: "We couldn't send your enquiry. Please try again or call us on 0800 009 2992.",
        };
      }

      // Fire Zapier webhook with every field; failures don't block the user
      const submittedAt = new Date().toISOString();
      const subject = `New ${insertRow.form_type} enquiry: ${insertRow.name ?? "Unknown"}${
        insertRow.postcode ? ` (${insertRow.postcode})` : ""
      }`;

      await fireEnquiryWebhook({
        subject,
        lead_id: id,
        submitted_at: submittedAt,
        form_type: insertRow.form_type,
        name: insertRow.name,
        phone: insertRow.phone,
        email: insertRow.email,
        postcode: insertRow.postcode,
        service_of_interest: insertRow.service_of_interest,
        services_interested: data.services_interested ?? [],
        homeowner_status: data.homeowner_status?.trim() || null,
        message: insertRow.message,
        source_page: insertRow.source_page,
        gclid: insertRow.gclid,
        user_agent: insertRow.user_agent,
        source: insertRow.traffic_source,
        source_medium: insertRow.traffic_medium,
        source_campaign: insertRow.traffic_campaign,
        source_referrer: insertRow.traffic_referrer,
        landing_page: insertRow.landing_page,
        ...trafficTermContentFromHeaders(getRequestHeader),
      });

      return { success: true as const, id };
    } catch (err) {
      console.error("submitEnquiry failed:", err);
      return {
        success: false as const,
        error: "We couldn't send your enquiry. Please try again or call us on 0800 009 2992.",
      };
    }
  });
